/* Free-Sample Lead Capture page.
 *
 * Anonymous visitor lands here → sees the active sample design's hero
 * card + "Verify mobile to download" CTA. The OTP modal handles
 * request-otp / verify-otp; on success the page calls
 * `POST /api/sample-design/claim` and reveals download buttons.
 *
 * After redemption, the page shows:
 *   • Download buttons for the sample plan files
 *   • Cross-sell row of related paid designs (same plot size / BHK)
 *   • Call + WhatsApp CTA to the configured support number
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import {
  Download,
  PhoneCall,
  MessageCircle,
  CheckCircle2,
  Sparkles,
  Loader2,
  ArrowRight,
  Building2,
} from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { useAuth } from "@/auth/AuthContext";
import { trackEvent } from "@/utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtSqft = (n) => `${Number(n || 0).toLocaleString("en-IN")} sq.ft.`;

const FreeSample = () => {
  const { user, status: authStatus } = useAuth();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [related, setRelated] = useState([]);
  const [claim, setClaim] = useState(null); // { claimed, design, claimed_at, replay }
  const [loadingCfg, setLoadingCfg] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "Free Sample House Plan · Mera Naksha";
  }, []);

  // Fetch the active sample design config (public).
  useEffect(() => {
    let cancel = false;
    axios
      .get(`${API}/sample-design/config`)
      .then(({ data }) => {
        if (!cancel) setCfg(data);
      })
      .catch(() => {
        if (!cancel) setCfg({ enabled: false });
      })
      .finally(() => {
        if (!cancel) setLoadingCfg(false);
      });
    return () => {
      cancel = true;
    };
  }, []);

  // Once authenticated, check claim status (and auto-fetch if already claimed).
  useEffect(() => {
    let cancel = false;
    if (authStatus !== "authenticated" || !cfg?.enabled) return undefined;
    axios
      .get(`${API}/sample-design/status`)
      .then(({ data }) => {
        if (cancel) return;
        if (data?.claimed) {
          // Auto-pull plan files (replay)
          axios
            .post(`${API}/sample-design/claim`)
            .then(({ data: r }) => {
              if (!cancel) setClaim(r);
            })
            .catch(() => {});
        }
      })
      .catch(() => {});
    return () => {
      cancel = true;
    };
  }, [authStatus, cfg?.enabled]);

  // Once we have the active sample design, fetch a few related paid designs
  // (same plot footprint, excluding the sample itself).
  useEffect(() => {
    let cancel = false;
    if (!cfg?.design?.id) return undefined;
    const { plot_width, plot_depth, id } = cfg.design;
    axios
      .get(`${API}/library`, { params: { page_size: 60 } })
      .then(({ data }) => {
        if (cancel) return;
        const candidates = (data?.items || []).filter(
          (d) => d.id !== id && Math.abs(d.plot_width - plot_width) <= 3,
        );
        // Prefer same BHK; cap to 4.
        candidates.sort((a, b) => {
          const sa = Math.abs(a.bhk - cfg.design.bhk);
          const sb = Math.abs(b.bhk - cfg.design.bhk);
          return sa - sb;
        });
        setRelated(candidates.slice(0, 4));
      })
      .catch(() => {});
    return () => {
      cancel = true;
    };
  }, [cfg?.design?.id, cfg?.design?.plot_width, cfg?.design?.plot_depth, cfg?.design?.bhk]);

  const handleClaim = async () => {
    setError(null);
    setClaiming(true);
    try {
      const { data } = await axios.post(`${API}/sample-design/claim`);
      setClaim(data);
      trackEvent("free_design_claimed", {
        design_id: data?.design?.id || cfg?.design?.id || "",
      });
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not claim — please retry");
    } finally {
      setClaiming(false);
    }
  };

  const startVerify = () => {
    navigate(`/customer/login?next=${encodeURIComponent("/free-sample")}`);
  };

  if (loadingCfg) {
    return (
      <div className="min-h-screen grid place-items-center text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!cfg?.enabled) {
    return (
      <div className="min-h-screen flex flex-col">
        <header className="border-b border-border bg-white">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <BrandLogo testId="free-sample-home-link" />
          </div>
        </header>
        <main className="flex-1 grid place-items-center px-4 text-center">
          <div className="max-w-md">
            <h1 className="text-2xl font-extrabold text-navy">
              Free sample campaign is not active right now.
            </h1>
            <p className="mt-2 text-muted-foreground">
              Browse the full Design Library to find a plan that fits your plot.
            </p>
            <Link
              to="/store"
              data-testid="free-sample-store-link"
              className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-navy text-gold font-extrabold hover:bg-navy-deep"
            >
              Go to Design Library <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const supportPhone = cfg.support_phone || "9717667115";
  const whatsappLink = `https://wa.me/91${supportPhone.replace(/\D/g, "")}?text=${encodeURIComponent(
    "Hi Mera Naksha — I'd like help picking a paid design for my plot",
  )}`;
  const callLink = `tel:+91${supportPhone}`;

  return (
    <div className="min-h-screen bg-sand-alt">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <BrandLogo testId="free-sample-home-link" />
          <Link
            to="/store"
            data-testid="free-sample-store-cta"
            className="text-[12.5px] font-bold text-navy hover:text-gold-dark inline-flex items-center gap-1"
          >
            Design Library <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Hero */}
        <div
          className="rounded-3xl bg-gradient-to-br from-navy via-navy-deep to-navy text-white p-6 sm:p-10 shadow-[0_24px_60px_-24px_rgba(11,37,69,0.45)] relative overflow-hidden"
          data-testid="free-sample-hero"
        >
          <div className="absolute -top-12 -right-12 h-48 w-48 rounded-full bg-gold/20 blur-3xl" />
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gold text-navy text-[11px] font-extrabold uppercase tracking-wider">
            <Sparkles className="h-3 w-3" /> Lead-Capture Promo
          </span>
          <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
            {cfg.headline}
          </h1>
          <p className="mt-3 max-w-2xl text-white/85 text-[14.5px] leading-relaxed">
            {cfg.subtext}
          </p>
        </div>

        {/* Design card */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-5 gap-5">
          <div className="lg:col-span-3 rounded-3xl bg-white border-2 border-border overflow-hidden">
            <div className="relative aspect-[4/3] bg-sand-alt">
              {cfg.design.image_url ? (
                <img
                  src={cfg.design.image_url}
                  alt={cfg.design.name}
                  data-testid="free-sample-image"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 grid place-items-center text-muted-foreground">
                  <Building2 className="h-12 w-12" />
                </div>
              )}
            </div>
            <div className="p-5">
              <div className="text-[11px] uppercase tracking-wider text-gold-dark font-bold">
                Sample Design
              </div>
              <h2 className="text-2xl font-extrabold text-navy mt-0.5" data-testid="free-sample-design-name">
                {cfg.design.name}
              </h2>
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[13px] text-muted-foreground">
                <span><strong className="text-navy">{cfg.design.size_label}</strong> plot</span>
                <span>·</span>
                <span><strong className="text-navy">{cfg.design.bhk} BHK</strong></span>
                <span>·</span>
                <span><strong className="text-navy">{cfg.design.num_floors}</strong> floor{cfg.design.num_floors > 1 ? "s" : ""}</span>
                <span>·</span>
                <span><strong className="text-navy">{fmtSqft((cfg.design.area_sqft || 0) * (cfg.design.num_floors || 1))}</strong> total built-up</span>
              </div>
            </div>
          </div>

          {/* Claim panel */}
          <div className="lg:col-span-2 rounded-3xl bg-white border-2 border-gold/40 p-5 sm:p-6 flex flex-col">
            {claim?.claimed ? (
              <ClaimedPanel
                claim={claim}
                onCall={callLink}
                onWhats={whatsappLink}
              />
            ) : authStatus === "authenticated" ? (
              <PreClaimPanel
                error={error}
                claiming={claiming}
                onClaim={handleClaim}
                userName={user?.name}
              />
            ) : (
              <VerifyPanel
                cfg={cfg}
                onStartVerify={startVerify}
              />
            )}
          </div>
        </div>

        {/* Cross-sell after claim */}
        {claim?.claimed && (
          <CrossSellSection
            related={related}
            supportPhone={supportPhone}
            callLink={callLink}
            whatsappLink={whatsappLink}
          />
        )}
      </main>
    </div>
  );
};

const VerifyPanel = ({ cfg, onStartVerify }) => (
  <>
    <div className="text-[11px] uppercase tracking-[0.18em] text-gold-dark font-extrabold">
      Step 1 of 2
    </div>
    <h3 className="mt-1 text-xl font-extrabold text-navy">
      Verify your mobile number
    </h3>
    <p className="mt-2 text-[13px] text-muted-foreground leading-relaxed">
      We send a one-time code to your mobile and unlock the sample.
      Limit: <strong className="text-navy">1 free sample per mobile</strong>.
    </p>
    <button
      onClick={onStartVerify}
      data-testid="free-sample-start-verify"
      className="mt-5 w-full min-h-[48px] rounded-xl bg-navy text-gold font-extrabold text-[14px] inline-flex items-center justify-center gap-2 hover:bg-navy-deep"
    >
      {cfg.cta_label}
      <ArrowRight className="h-4 w-4" />
    </button>
    <ul className="mt-4 space-y-1.5 text-[12px] text-muted-foreground">
      <li className="flex items-start gap-1.5">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
        100% free · No card / payment required
      </li>
      <li className="flex items-start gap-1.5">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
        Architect-quality drawing format
      </li>
      <li className="flex items-start gap-1.5">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
        Instant download after OTP
      </li>
    </ul>
  </>
);

const PreClaimPanel = ({ error, claiming, onClaim, userName }) => (
  <>
    <div className="text-[11px] uppercase tracking-[0.18em] text-gold-dark font-extrabold">
      Step 2 of 2 · You&apos;re verified ✓
    </div>
    <h3 className="mt-1 text-xl font-extrabold text-navy">
      {userName ? `Hi ${userName.split(" ")[0]} —` : "Welcome —"} claim your free sample
    </h3>
    <p className="mt-2 text-[13px] text-muted-foreground leading-relaxed">
      One tap and the PDFs land in your downloads. This is your single
      free sample for the campaign.
    </p>
    <button
      onClick={onClaim}
      disabled={claiming}
      data-testid="free-sample-claim-btn"
      className="mt-5 w-full min-h-[48px] rounded-xl bg-gold text-navy font-extrabold text-[14px] inline-flex items-center justify-center gap-2 hover:bg-gold-dark hover:text-white disabled:opacity-60"
    >
      {claiming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
      {claiming ? "Claiming…" : "Claim free sample"}
    </button>
    {error && (
      <div className="mt-3 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="free-sample-error">
        {error}
      </div>
    )}
  </>
);

const ClaimedPanel = ({ claim, onCall, onWhats }) => (
  <div data-testid="free-sample-claimed-panel" className="flex-1 flex flex-col">
    <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-700 font-extrabold inline-flex items-center gap-1.5">
      <CheckCircle2 className="h-3.5 w-3.5" /> Sample unlocked
    </div>
    <h3 className="mt-1 text-xl font-extrabold text-navy">
      Your PDFs are ready
    </h3>
    <p className="mt-2 text-[13px] text-muted-foreground leading-snug">
      {claim.replay
        ? "You've already claimed this sample — here are the files again."
        : "Tap each file to download. We've recorded your redemption."}
    </p>
    <div className="mt-4 space-y-2">
      {(claim.design.plan_files || []).map((f, i) => (
        <a
          key={f.url || `plan-${i}`}
          href={f.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`free-sample-download-${i}`}
          className="flex items-center gap-2 min-h-[48px] px-4 rounded-xl bg-navy text-gold font-extrabold text-[13.5px] hover:bg-navy-deep"
        >
          <Download className="h-4 w-4" />
          {f.label || `Sample Plan ${i + 1}`}
        </a>
      ))}
    </div>
    <div className="mt-5 pt-4 border-t border-border grid grid-cols-2 gap-2">
      <a
        href={onCall}
        data-testid="free-sample-call-cta"
        className="min-h-[44px] rounded-xl bg-emerald-600 text-white font-extrabold text-[12.5px] inline-flex items-center justify-center gap-1.5 hover:bg-emerald-700"
      >
        <PhoneCall className="h-3.5 w-3.5" /> Call us
      </a>
      <a
        href={onWhats}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="free-sample-whatsapp-cta"
        className="min-h-[44px] rounded-xl bg-[#25D366] text-white font-extrabold text-[12.5px] inline-flex items-center justify-center gap-1.5 hover:opacity-90"
      >
        <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
      </a>
    </div>
  </div>
);

const CrossSellSection = ({ related, supportPhone, callLink, whatsappLink }) => (
  <section className="mt-10" data-testid="free-sample-cross-sell">
    <div className="flex items-end justify-between gap-3 flex-wrap">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-gold-dark font-extrabold">
          Up next
        </div>
        <h2 className="text-2xl sm:text-3xl font-extrabold text-navy mt-0.5">
          Browse premium designs for your plot
        </h2>
        <p className="text-[13.5px] text-muted-foreground mt-1 max-w-2xl">
          Pick from {related.length || "40+"} curated paid plans that match your plot
          size. Need help choosing? Our team is one tap away.
        </p>
      </div>
      <Link
        to="/store"
        data-testid="free-sample-browse-all"
        className="min-h-[44px] px-4 rounded-xl bg-navy text-gold font-extrabold text-[13px] inline-flex items-center gap-1.5 hover:bg-navy-deep"
      >
        See full library <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>

    {related.length > 0 && (
      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
        {related.map((d) => (
          <Link
            key={d.id}
            to={`/store?design=${d.id}`}
            data-testid={`free-sample-related-${d.id}`}
            className="block rounded-2xl bg-white border-2 border-border overflow-hidden hover:border-gold/60 transition-colors"
          >
            <div className="aspect-[4/3] bg-sand-alt">
              {d.image_url ? (
                <img src={d.image_url} alt={d.name} className="w-full h-full object-cover" />
              ) : null}
            </div>
            <div className="p-3">
              <div className="text-[13px] font-extrabold text-navy truncate">{d.name}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {d.size_label} · {d.bhk} BHK
              </div>
              <div className="mt-1.5 text-[12.5px] font-extrabold text-gold-dark">
                ₹{Number(d.price).toLocaleString("en-IN")}
              </div>
            </div>
          </Link>
        ))}
      </div>
    )}

    {/* Support strip */}
    <div className="mt-6 rounded-3xl bg-navy text-white p-5 sm:p-6 flex flex-wrap items-center justify-between gap-3">
      <div>
        <div className="text-[11px] uppercase tracking-wider text-gold font-extrabold">
          Need help picking?
        </div>
        <div className="text-lg sm:text-xl font-extrabold mt-0.5">
          Call <span className="text-gold">+91 {supportPhone}</span> or WhatsApp us
        </div>
      </div>
      <div className="flex gap-2">
        <a
          href={callLink}
          className="min-h-[44px] px-4 rounded-xl bg-gold text-navy font-extrabold text-[13px] inline-flex items-center gap-1.5 hover:bg-gold-dark hover:text-white"
        >
          <PhoneCall className="h-3.5 w-3.5" /> Call us
        </a>
        <a
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          className="min-h-[44px] px-4 rounded-xl bg-[#25D366] text-white font-extrabold text-[13px] inline-flex items-center gap-1.5 hover:opacity-90"
        >
          <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
        </a>
      </div>
    </div>
  </section>
);

export default FreeSample;
